"use client";

import dynamic from "next/dynamic";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { ChartCard } from "@/components/ui/chart-card";
import { ChartSeriesLegend, ChartTooltipCard } from "@/components/ui/chart-tooltip-card";
import { ExportCsvButton } from "@/components/ui/export-csv-button";
import { AppEmbedButton } from "@/components/embed/AppEmbedButton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAppCore } from "@/hooks/use-app-core";
import { useApplePage } from "@/hooks/use-apple-page";
import { useAppRatingsHistory } from "@/hooks/use-app-ratings-history";
import { useAppWorldwideRatings } from "@/hooks/use-app-worldwide-ratings";
import { calculateDateTicks, formatIsoDay, formatTooltipLabel, spansMoreThanAYear } from "@/lib/app-detail/chart-utils";
import { isoDay } from "@/lib/app-detail/daily-buckets";
import { toGlobalSeries, toStorefrontSeries, type ScopedRatingsPoint } from "@/lib/app-detail/ratings-scope";
import type { StorefrontRating } from "@/lib/app-detail/ratings-history";
import { countryCodeToFlagEmoji, getCountryName } from "@/lib/countries";
import { csvFileName, type CsvColumn } from "@/lib/export/csv";
import { formatNumber } from "@/lib/utils";

// recharts is heavy and browser-only; same on-demand pattern as app-charts.tsx.
const CartesianGrid = dynamic(() => import("recharts").then((m) => m.CartesianGrid), { ssr: false }) as typeof import("recharts").CartesianGrid;
const ComposedChart = dynamic(() => import("recharts").then((m) => m.ComposedChart), { ssr: false }) as typeof import("recharts").ComposedChart;
const Line = dynamic(() => import("recharts").then((m) => m.Line), { ssr: false }) as typeof import("recharts").Line;
const ResponsiveContainer = dynamic(() => import("recharts").then((m) => m.ResponsiveContainer), { ssr: false }) as typeof import("recharts").ResponsiveContainer;
const Tooltip = dynamic(() => import("recharts").then((m) => m.Tooltip), { ssr: false }) as typeof import("recharts").Tooltip;
const XAxis = dynamic(() => import("recharts").then((m) => m.XAxis), { ssr: false }) as typeof import("recharts").XAxis;
const YAxis = dynamic(() => import("recharts").then((m) => m.YAxis), { ssr: false }) as typeof import("recharts").YAxis;

const SUBTITLE = "Average rating and rating count over time. Global reads the worldwide count; a storefront reads its own App Store ratings. No worldwide average is recorded.";
const RATING_STROKE = "var(--accent-primary, #10b981)";
const COUNT_STROKE = "#8b8fd9";
const VISIBLE_ROWS = 5;

const GLOBAL = "global";

export type RatingsCsvRow = { country: string; date: string; row_type: "history" | "star" | "storefront"; average_rating?: number | null; rating_count?: number | null; star?: number };

const CSV_COLUMNS = (appId: number): CsvColumn<RatingsCsvRow>[] => [
	{ key: "app_store_id", value: () => appId },
	"country", "date", "row_type", "average_rating", "rating_count", "star",
];

/**
 * One file for everything the card draws: the history series, Apple's five star counts
 * (dated by the Apple fetch), and every By-storefront row, not only the visible five.
 */
export function ratingsCsvRows(
	history: readonly ScopedRatingsPoint[],
	historyCountry: string,
	apple: { ratingCounts: number[] | null; fetchedAt: string },
	starsCountry: string,
	byCountry: readonly StorefrontRating[],
): RatingsCsvRow[] {
	return [
		...history.map((p) => ({ country: historyCountry, date: p.date, row_type: "history" as const, average_rating: p.average, rating_count: p.count })),
		...(apple.ratingCounts ?? []).map((n, i) => ({ country: starsCountry, date: apple.fetchedAt.slice(0, 10), row_type: "star" as const, rating_count: n, star: 5 - i })),
		...byCountry.map((r) => ({ country: r.country_code, date: isoDay(r.asOf), row_type: "storefront" as const, average_rating: r.average, rating_count: r.count })),
	];
}

// The file is named after the app, and the name is a /core read: only the app page, which has the button, makes it.
function RatingsCsvExport({ appId, picked, rows, disabled }: { appId: number; picked: string; rows: readonly RatingsCsvRow[]; disabled: boolean }) {
	const appName = useAppCore(appId).data?.name;
	return <ExportCsvButton rows={rows} columns={CSV_COLUMNS(appId)} fileName={csvFileName("ratings", [appName, appId, picked])} disabled={disabled} />;
}

const avg = (v: unknown) => (typeof v === "number" ? v.toFixed(2) : "—");
const count = (v: unknown) => (typeof v === "number" ? v.toLocaleString("en-US") : "—");

function StorefrontTable({ rows }: { rows: readonly StorefrontRating[] }) {
	const [showAll, setShowAll] = useState(false);
	if (rows.length === 0) return null;
	const visible = showAll ? rows : rows.slice(0, VISIBLE_ROWS);
	return (
		<div className="mt-3">
			{/* Smaller type and tighter cells on a phone: four columns with a full "Oct 5, 2025" date fit in 348 px. */}
			<Table className="text-xs sm:text-sm [&_td]:px-1 [&_th]:px-1 sm:[&_td]:px-2 sm:[&_th]:px-2">
				<TableHeader>
					<TableRow>
						<TableHead>Storefront</TableHead>
						<TableHead className="text-right">Rating</TableHead>
						<TableHead className="text-right">Ratings</TableHead>
						<TableHead className="text-right">As of</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{visible.map((r) => (
						<TableRow key={r.country_code}>
							<TableCell className="max-w-[6.5rem] truncate sm:max-w-none" title={getCountryName(r.country_code) ?? undefined}>
								{countryCodeToFlagEmoji(r.country_code)} {getCountryName(r.country_code) ?? r.country_code.toUpperCase()}
							</TableCell>
							<TableCell className="text-right tabular-nums">{avg(r.average)}</TableCell>
							<TableCell className="text-right tabular-nums">{count(r.count)}</TableCell>
							<TableCell className="whitespace-nowrap text-right text-muted-foreground" title={r.asOf}>{r.asOf.startsWith(String(new Date().getUTCFullYear())) ? formatIsoDay(isoDay(r.asOf)) : formatTooltipLabel(isoDay(r.asOf))}</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
			{rows.length > VISIBLE_ROWS && (
				<div className="mt-2 flex justify-center">
					<Button variant="outline" size="sm" onClick={() => setShowAll((v) => !v)}>
						{showAll ? "Show fewer" : "Show all"}
					</Button>
				</div>
			)}
		</div>
	);
}

/**
 * One Ratings card (SPEC 07 + SPEC 25 merged, owner 2026-09-16): a Global / storefront picker,
 * the average and count over time, Apple's star counts for the shown storefront in the hover (SPEC-1140), and one CSV. Always the whole history; ?range= is not read here.
 */
export function AppRatingsCard({ appId, country, className, variant = "main" }: { appId: number; country: string; className?: string; variant?: "main" | "embed" }) {
	// Global by default (owner 2026-09-16): the count is the worldwide one until a storefront is picked.
	const [picked, setPicked] = useState<string>(GLOBAL);
	const scope = picked === GLOBAL ? "global" : "storefront";
	const storefront = useAppRatingsHistory(appId, scope === "global" ? country : picked, "all");
	const global = useAppWorldwideRatings(appId, country, "all", scope === "global");
	const items = useMemo(
		() => (scope === "global" ? toGlobalSeries(global.data.items) : toStorefrontSeries(storefront.data.items)),
		[scope, global.data.items, storefront.data.items]
	);
	const active = scope === "global" ? global : storefront;
	// The storefront Apple's star counts are read for: the page one under Global, else the picked one.
	const starsCc = (scope === "global" ? country : picked).toLowerCase();
	const { data: apple } = useApplePage(appId, starsCc);
	const csvRows = useMemo(
		() => ratingsCsvRows(items, picked, apple, starsCc, storefront.data.byCountry),
		[items, picked, apple, starsCc, storefront.data.byCountry]
	);
	const withYear = spansMoreThanAYear(items);
	const latest = items.length ? items[items.length - 1] : null;
	const cc = picked.toUpperCase();
	const scopeLabel = scope === "global" ? "Global" : (getCountryName(picked) ?? cc);

	const tooltip = ({ active: on, payload, label }: { active?: boolean; payload?: readonly { payload?: unknown }[]; label?: unknown }) => {
		const point = on && payload && payload.length ? (payload[0].payload as ScopedRatingsPoint) : null;
		if (!point) return null;
		// Apple publishes no star history: every day shows today's counts, and the footer dates them.
		const stars = apple.ratingCounts && apple.totalRatings ? apple.ratingCounts : null;
		const rows = [
			...(point.average != null ? [{ key: "avg", color: RATING_STROKE, name: "Average", value: avg(point.average) }] : []),
			{ key: "count", color: COUNT_STROKE, name: "Ratings", value: count(point.count) },
			...(stars ?? []).map((n, i) => ({ key: `star${5 - i}`, name: `${5 - i} ⭐`, value: count(n) })),
		];
		const footer = stars ? `Stars: ${countryCodeToFlagEmoji(starsCc)} ${getCountryName(starsCc) ?? starsCc.toUpperCase()} · App Store, ${formatTooltipLabel(apple.fetchedAt.slice(0, 10))}` : undefined;
		return <ChartTooltipCard label={String(label)} rows={rows} footer={footer} />;
	};

	const action = (
		<div className="flex flex-wrap items-center justify-end gap-1.5">
			<Select value={picked} onValueChange={setPicked}>
				<SelectTrigger className="h-10 md:h-8 w-[190px] text-xs" aria-label="Storefront" data-testid="ratings-storefront">
					<SelectValue />
				</SelectTrigger>
				<SelectContent>
					<SelectItem value={GLOBAL}>Global</SelectItem>
					{storefront.data.byCountry.map((r) => (
						<SelectItem key={r.country_code} value={r.country_code}>
							{countryCodeToFlagEmoji(r.country_code)} {getCountryName(r.country_code) ?? r.country_code}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
			{/* An embed has no sign-in, which the CSV needs, so it gets neither button. */}
			{variant === "main" && (
				<>
					<RatingsCsvExport appId={appId} picked={picked} rows={csvRows} disabled={active.isLoading || active.isFetching} />
					<AppEmbedButton type="ratings" appId={appId} country={country} />
				</>
			)}
		</div>
	);

	return (
		<ChartCard title="Ratings" subtitle={SUBTITLE} terminalCode="APP.RATINGS" action={action} isLoading={active.isLoading} className={className}>
			{active.error ? (
				<p className="flex h-40 items-center justify-center text-sm text-red-500" role="alert" data-testid="ratings-error">The {scopeLabel} rating history could not be loaded, so this card is empty.</p>
			) : !latest ? (
				<p className="flex h-40 items-center justify-center text-sm text-muted-foreground">No rating history for {scopeLabel} yet.</p>
			) : (
				<>
					<div data-testid="ratings-chart" data-scope={scope}>
						<div className="h-36">
							<ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 1, height: 1 }}>
								<ComposedChart data={items} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
									<CartesianGrid vertical={false} stroke="#333" strokeDasharray="3 3" strokeOpacity={0.4} />
									<XAxis dataKey="date" tickLine={false} axisLine={false} tickMargin={8} tick={{ fill: "#999", fontSize: 10 }} ticks={calculateDateTicks(items, 5)} tickFormatter={(iso: string) => formatIsoDay(iso, withYear)} />
									<YAxis yAxisId="count" width={44} domain={["auto", "auto"]} tickLine={false} axisLine={false} tick={{ fill: "#999", fontSize: 10 }} tickFormatter={(v: number) => formatNumber(v)} />
									{scope === "storefront" && <YAxis yAxisId="rating" orientation="right" width={32} domain={[1, 5]} tickLine={false} axisLine={false} tick={{ fill: "#999", fontSize: 10 }} tickFormatter={(v: number) => v.toFixed(1)} />}
									<Tooltip content={tooltip} />
									<Line yAxisId="count" type="monotone" dataKey="count" stroke={COUNT_STROKE} strokeWidth={2} dot={items.length < 12 ? { r: 3, fill: COUNT_STROKE } : false} connectNulls isAnimationActive={false} />
									{scope === "storefront" && <Line yAxisId="rating" type="monotone" dataKey="average" stroke={RATING_STROKE} strokeWidth={2} strokeDasharray="4 4" dot={false} connectNulls isAnimationActive={false} />}
								</ComposedChart>
							</ResponsiveContainer>
						</div>
					</div>
					{scope === "storefront" && <ChartSeriesLegend className="mt-1" items={[{ key: "avg", color: RATING_STROKE, label: "Average (right)" }, { key: "count", color: COUNT_STROKE, label: `Ratings in ${cc}` }]} />}
				</>
			)}
			<StorefrontTable rows={storefront.data.byCountry} />
		</ChartCard>
	);
}
